/**
 * Minimal CMP (Consent Management Platform) simulation.
 *
 * Three modes, driven by the `consent` URL param:
 *   - granted : inject TCF v2 __tcfapi that returns consent=true for all
 *               purposes and vendors. The banner is not shown because
 *               consent is pre-granted.
 *   - refused : inject TCF v2 __tcfapi that returns consent=false. Banner
 *               not shown (user already decided).
 *   - absent  : no __tcfapi injected at all. The banner is shown so the
 *               runner can choose to interact with it (or not) during the
 *               scenario. Measurers encountering no TCF signal must fall
 *               back to "exempt mode" per CNIL guidelines.
 *
 * The runner also has a stronger injection path (consent/inject.ts via
 * addInitScript) used BEFORE any scripts on the page run. This CMP module
 * is the in-page equivalent: it remains useful when the test app is
 * embedded in an environment where addInitScript isn't available (Tizen,
 * manual browser testing).
 *
 * Whichever path wins first, the result is the same: __tcfapi exists (or
 * not) and returns a consistent TCF v2 response.
 */

const DEFAULT_TCSTRING =
  'CPVSHIsAAAAABAwABBENBrCsAP_AAH_AAAAAIGNf_X__b3_j-_7_f_t0eY1P9_7__-0zjhfdt-8N3f_X_L8X52M7vF36pq4KuR4Eu3LBIQdlHOHcTUmw6okVrTPsak2Mr7NKJ7PEmnMbe2dYGHtfn91T_Z2Z76_Xf_-v_-f___d___1-_BBAAIHAYQCAAAAAAAAAAAAAAAAAAAAAAAA';

/**
 * Entry point called from main.js.
 * @param {"granted"|"refused"|"absent"} mode
 */
export function setupCmp(mode) {
  if (mode === 'granted' || mode === 'refused') {
    installTcfShim(mode === 'granted');
    hideBanner();
  } else {
    // absent: do NOT install __tcfapi. Show the banner so the runner
    // can optionally click accept/refuse, which will THEN install the
    // shim dynamically based on the button.
    showBanner();
    wireBannerButtons();
  }

  // Expose state for Playwright + diagnostics.
  window.__CMP_STATE__ = {
    mode,
    tcfapiInstalled: typeof window.__tcfapi === 'function',
  };
}

function hideBanner() {
  const el = document.getElementById('cmp-banner');
  if (el) el.classList.remove('visible');
}
function showBanner() {
  const el = document.getElementById('cmp-banner');
  if (el) el.classList.add('visible');
}
function wireBannerButtons() {
  const el = document.getElementById('cmp-banner');
  if (!el) return;
  el.addEventListener(
    'click',
    (e) => {
      const btn = e.target instanceof HTMLElement ? e.target.closest('[data-cmp]') : null;
      if (!btn) return;
      const action = btn.getAttribute('data-cmp');
      if (action === 'accept') installTcfShim(true);
      if (action === 'refuse') installTcfShim(false);
      hideBanner();
      window.__CMP_STATE__ = {
        mode: action === 'accept' ? 'granted' : 'refused',
        tcfapiInstalled: typeof window.__tcfapi === 'function',
        interactedAt: Date.now(),
      };
    },
    { once: false },
  );
}

/**
 * Install the TCF v2 __tcfapi shim. Kept in sync with
 * apps/runner/src/consent/inject.ts (simpler variant - runner version is
 * authoritative because it runs earlier via addInitScript).
 */
function installTcfShim(allow) {
  if (typeof window.__tcfapi === 'function') return;

  const purposes = {};
  const vendors = {};
  for (let i = 1; i <= 10; i++) purposes[i] = allow;
  for (let i = 1; i <= 1000; i++) vendors[i] = allow;

  const listeners = new Map();
  let nextId = 1;

  const tcData = {
    tcString: DEFAULT_TCSTRING,
    tcfPolicyVersion: 4,
    cmpId: 999,
    cmpVersion: 1,
    gdprApplies: true,
    eventStatus: 'tcloaded',
    cmpStatus: 'loaded',
    listenerId: null,
    isServiceSpecific: true,
    purposeOneTreatment: false,
    useNonStandardTexts: false,
    publisherCC: 'FR',
    purpose: { consents: purposes, legitimateInterests: purposes },
    vendor: { consents: vendors, legitimateInterests: vendors },
    specialFeatureOptins: purposes,
    publisher: {
      consents: purposes,
      legitimateInterests: purposes,
      customPurpose: { consents: {}, legitimateInterests: {} },
      restrictions: {},
    },
  };

  function tcfapi(cmd, _version, callback, parameter) {
    if (typeof callback !== 'function') return;
    switch (cmd) {
      case 'ping':
        callback(
          {
            gdprApplies: true,
            cmpLoaded: true,
            cmpStatus: 'loaded',
            displayStatus: 'hidden',
            apiVersion: '2.2',
            cmpVersion: 1,
            cmpId: 999,
            gvlVersion: 300,
            tcfPolicyVersion: 4,
          },
          true,
        );
        break;
      case 'getTCData':
        callback({ ...tcData, listenerId: null }, true);
        break;
      case 'addEventListener': {
        const id = nextId++;
        listeners.set(id, callback);
        callback({ ...tcData, listenerId: id }, true);
        break;
      }
      case 'removeEventListener': {
        const id = parameter;
        const removed = listeners.delete(id);
        callback(removed, true);
        break;
      }
      case 'getInAppTCData':
        callback(tcData, true);
        break;
      default:
        callback(null, false);
    }
  }

  try {
    Object.defineProperty(window, '__tcfapi', {
      value: tcfapi,
      writable: false,
      configurable: false,
    });
  } catch {
    window.__tcfapi = tcfapi;
  }
}
